import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { credentialingService } from '../../../services/credentialingService';

const InsuranceCredentialingPanel = ({ refreshTrigger }) => {
  const [credentialing, setCredentialing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCredentialing();
  }, [refreshTrigger]);

  const loadCredentialing = async () => {
    try {
      setLoading(true);
      const data = await credentialingService?.getInsuranceCredentialing();
      setCredentialing(data);
    } catch (err) {
      setError(err?.message || 'Failed to load insurance credentialing');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success border-success';
      case 'in_progress':
        return 'bg-primary/10 text-primary border-primary';
      case 'submitted':
        return 'bg-warning/10 text-warning border-warning';
      case 'denied':
        return 'bg-destructive/10 text-destructive border-destructive';
      case 'expired':
        return 'bg-accent/10 text-accent border-accent';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {credentialing?.map((item) => (
          <div key={item?.id} className="bg-muted/30 rounded-lg border border-border p-4 hover:shadow-elevation-2 transition-smooth">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                  <Icon name="Shield" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">{item?.insuranceNetwork}</h3>
                  <p className="text-xs text-muted-foreground">Applied {formatDate(item?.applicationDate)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item?.status)}`}>
                  {item?.status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </span>
              </div>

              {item?.providerNumber && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Provider Number</span>
                  <span className="text-xs font-medium text-foreground font-mono">{item?.providerNumber}</span>
                </div>
              )}

              {item?.reimbursementRate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Reimbursement Rate</span>
                  <span className="text-xs font-medium text-foreground">{item?.reimbursementRate}%</span>
                </div>
              )}

              {item?.approvalDate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Approved</span>
                  <span className="text-xs text-foreground">{formatDate(item?.approvalDate)}</span>
                </div>
              )}

              {item?.expirationDate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Expires</span>
                  <span className="text-xs text-foreground">{formatDate(item?.expirationDate)}</span>
                </div>
              )}

              {item?.directoryUpdated !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Directory Status</span>
                  <div className="flex items-center gap-1">
                    <Icon 
                      name={item?.directoryUpdated ? 'CheckCircle' : 'Clock'} 
                      size={14} 
                      className={item?.directoryUpdated ? 'text-success' : 'text-warning'} 
                    />
                    <span className={`text-xs ${item?.directoryUpdated ? 'text-success' : 'text-warning'}`}>
                      {item?.directoryUpdated ? 'Updated' : 'Pending'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              <Button variant="ghost" size="sm" iconName="Eye" iconPosition="left">
                View Details
              </Button>
              <Button variant="ghost" size="sm" iconName="Edit" />
            </div>
          </div>
        ))}
      </div>

      {credentialing?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <Icon name="Shield" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-base font-medium text-foreground mb-2">No Insurance Credentialing</p>
          <p className="text-sm text-muted-foreground mb-4">Start credentialing with insurance networks</p>
          <Button variant="outline" iconName="Plus" iconPosition="left">
            Add Insurance Network
          </Button>
        </div>
      )}
    </div>
  );
};

export default InsuranceCredentialingPanel;